import React, { useEffect, useState } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import Tooltip from '@mui/material/Tooltip';
import { Col, Row } from 'reactstrap';
import { Badge } from 'reactstrap';
import moment from 'moment';
import AppConfig from 'constants/config';
import axios from 'axios';

function OrderDetailsPopup({ open, onClose, orderId }) {
  const [loading, setLoading] = useState(false);
  const [orderDetails, setOrderDetails] = useState(null);

  useEffect(() => {
    if (open && orderId) {
      fetchOrderDetails();
    }
    if (!open) {
      setOrderDetails(null);
    }
  }, [open, orderId]);

  const fetchOrderDetails = async () => {
    setLoading(true);
    try {
      const result = await axios.post(
        `${AppConfig.baseUrl}order/getOrderDetails`,
        { orderId },
        {
          headers: {
            'Content-Type': 'application/json',
            token: localStorage.getItem('admin_token'),
          },
        }
      );
      if (result.data && !result.data.error) {
        setOrderDetails(result.data.detail);
      }
    } catch (error) {
      console.error('Failed to fetch order details', error);
    }
    setLoading(false);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'New': return 'primary';
      case 'Processed': return 'info';
      case 'Shipped': return 'warning';
      case 'Delivered': return 'success';
      case 'Cancelled': return 'danger';
      case 'RTO': return 'danger';
      default: return 'secondary';
    }
  };

  const getFinalValue = (data) => {
    let tempValue =
      data.discount_name &&
      (data.discount_name === 'Same' || data.discount_name === 'SameAndDiscount')
        ? ((data.quantity / data.discount_on_product.purchase) * data.discount_on_product.bonus + Number(data.quantity)) *
          (data.ePTR ? data.ePTR : data.PTR)
        : (data.ePTR ? data.ePTR : data.PTR) * data.quantity;
    let gstValue = data.GST ? data.GST : data.inventory_id && data.inventory_id.product_id && data.inventory_id.product_id.GST ? data.inventory_id.product_id.GST.value : 0;
    let finalValue = (Number(gstValue) / 100) * tempValue + tempValue;
    return finalValue;
  };

  const getOfferText = (p) => {
    if (p.discount_name === 'SameAndDiscount') {
      return `${p.discount_on_product.purchase}+${p.discount_on_product.bonus} (Bonus) and ${p.discount_per}% Off`;
    }
    if (p.discount_name === 'DifferentAndDiscount') {
      return `${p.discount_on_product.purchase}+${p.discount_on_product.bonus} ${p.discount_on_product.name} and ${p.discount_per}% Off`;
    }
    if (p.discount_name === 'Same') {
      return `${p.discount_on_product.purchase}+${p.discount_on_product.bonus} (Bonus)`;
    }
    if (p.discount_name === 'Different') {
      return `${p.discount_on_product.purchase}+${p.discount_on_product.bonus} ${p.discount_on_product.name}`;
    }
    return `${p.discount_per}% discount`;
  };

  const getPaymentTypeLabel = () => {
    if (!orderDetails) return '';
    if (orderDetails.isBulk) return 'Bulk Prepaid';
    if (orderDetails.paymentType === 'Online') {
      let label = 'Prepaid';
      if (orderDetails.paymentStatus === 'success' && orderDetails.rawTransaction && orderDetails.rawTransaction.payment_mode) {
        label += '-' + orderDetails.rawTransaction.payment_mode;
      }
      return label;
    }
    return orderDetails.paymentType;
  };

  const od = orderDetails;

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg">
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        Order Details {od ? `- ${od.order_id}` : ''}
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}>
            <CircularProgress />
          </div>
        ) : od ? (
          <>
            {/* Order Info Header */}
            <div
              style={{
                background: '#3f51b5',
                color: '#fff',
                borderRadius: 8,
                padding: '20px 24px',
                marginBottom: 20,
              }}
            >
              <Row>
                {/* ORDER PLACED BY */}
                <Col xs={12} md={3}>
                  <h6 style={{ fontWeight: 'bold', marginBottom: 10 }}>ORDER PLACED BY</h6>
                  <div style={{ fontSize: 13 }}>
                    <div className="mb-1">
                      <i className="zmdi zmdi-info-outline zmdi-hc-fw zmdi-hc-sm" />
                      <span className="ml-1">{od.order_id}</span>
                    </div>
                    <div className="mb-1">
                      <i className="zmdi zmdi-account-o zmdi-hc-fw zmdi-hc-sm" />
                      <span className="ml-1">{od.user_id ? od.user_id.company_name : 'N/A'}</span>
                    </div>
                    <div className="mb-1">
                      <i className="zmdi zmdi-pin zmdi-hc-fw zmdi-hc-sm" />
                      <span className="ml-1">{od.user_id ? od.user_id.user_address : 'N/A'}</span>
                    </div>
                    <div className="mb-1">
                      <i className="zmdi zmdi-city zmdi-hc-fw zmdi-hc-sm" />
                      <span className="ml-1">
                        {od.user_id && od.user_id.user_city ? od.user_id.user_city : ''}
                        {od.user_id && od.user_id.user_state ? `, ${od.user_id.user_state.name}` : ''}
                      </span>
                      <div style={{ marginLeft: 20 }}>{od.user_id ? od.user_id.user_pincode : ''}</div>
                    </div>
                    <div className="mb-1">
                      <i className="zmdi zmdi-phone zmdi-hc-fw zmdi-hc-sm" />
                      <span className="ml-1">{od.user_id ? od.user_id.phone : 'N/A'}</span>
                    </div>
                  </div>
                </Col>

                {/* ORDER PLACED TO */}
                <Col xs={12} md={3}>
                  <h6 style={{ fontWeight: 'bold', marginBottom: 10 }}>ORDER PLACED TO</h6>
                  <div style={{ fontSize: 13 }}>
                    <div className="mb-1">
                      <i className="zmdi zmdi-account-o zmdi-hc-fw zmdi-hc-sm" />
                      <span className="ml-1">{od.seller_id ? od.seller_id.company_name : 'N/A'}</span>
                    </div>
                    <div className="mb-1">
                      <i className="zmdi zmdi-pin zmdi-hc-fw zmdi-hc-sm" />
                      <span className="ml-1">{od.seller_id ? od.seller_id.user_address : 'N/A'}</span>
                    </div>
                    <div className="mb-1">
                      <i className="zmdi zmdi-city zmdi-hc-fw zmdi-hc-sm" />
                      <span className="ml-1">
                        {od.seller_id && od.seller_id.user_state ? od.seller_id.user_state.name : 'N/A'}
                      </span>
                      <div style={{ marginLeft: 20 }}>{od.seller_id ? od.seller_id.user_pincode : ''}</div>
                    </div>
                    <div className="mb-1">
                      <i className="zmdi zmdi-phone zmdi-hc-fw zmdi-hc-sm" />
                      <span className="ml-1">{od.seller_id ? od.seller_id.phone : 'N/A'}</span>
                    </div>
                  </div>
                </Col>

                {/* BUYER KYC & PAYMENT */}
                <Col xs={12} md={3}>
                  <h6 style={{ fontWeight: 'bold', marginBottom: 10 }}>BUYER KYC DETAILS</h6>
                  <div style={{ fontSize: 13 }}>
                    {od.user_id && od.user_id.drugLic20B && od.user_id.drugLic20B.name && (
                      <div className="mb-1">
                        FORM 20{' '}
                        <span style={{ fontSize: 11 }}>
                          ({od.user_id.drugLic20B.lic || ''} | {od.user_id.drugLic20B.expires ? moment(od.user_id.drugLic20B.expires).format('DD/MM/YYYY') : ''})
                        </span>
                      </div>
                    )}
                    {od.user_id && od.user_id.drugLic21B && od.user_id.drugLic21B.name && (
                      <div className="mb-1">
                        FORM 21{' '}
                        <span style={{ fontSize: 11 }}>
                          ({od.user_id.drugLic21B.lic || ''} | {od.user_id.drugLic21B.expires ? moment(od.user_id.drugLic21B.expires).format('DD/MM/YYYY') : ''})
                        </span>
                      </div>
                    )}
                    {od.user_id && od.user_id.fassaiLic && od.user_id.fassaiLic.name && (
                      <div className="mb-1">
                        FASSAI{' '}
                        <span style={{ fontSize: 11 }}>
                          ({od.user_id.fassaiLic.lic || ''} | {od.user_id.fassaiLic.expires ? moment(od.user_id.fassaiLic.expires).format('DD/MM/YYYY') : ''})
                        </span>
                      </div>
                    )}
                    {od.user_id && od.user_id.gstLic && od.user_id.gstLic.name && (
                      <div className="mb-1">
                        GSTN <span style={{ fontSize: 11 }}>({od.user_id.gstLic.lic || ''})</span>
                      </div>
                    )}
                    <div className="mb-1" style={{ marginTop: 8 }}>
                      Payment Type: <strong>{getPaymentTypeLabel()}</strong>
                    </div>
                    {od.delivery_charges > 0 && (
                      <div className="mb-1">Delivery Charges: &#x20B9;{od.delivery_charges}</div>
                    )}
                    <div className="mb-1">
                      Total: <strong>&#x20B9;{Number(od.total_amount).toLocaleString('en-IN', { maximumFractionDigits: 2, minimumFractionDigits: 2 })}</strong>
                    </div>
                    {od.paymentType === 'Partial' && od.partialPyament && (
                      <div className="mb-1">
                        Prepaid: &#x20B9;{Number(od.prepayAmt).toLocaleString('en-IN', { maximumFractionDigits: 2, minimumFractionDigits: 2 })}
                      </div>
                    )}
                  </div>
                </Col>

                {/* ORDER INSIGHT */}
                <Col xs={12} md={3}>
                  <h6 style={{ fontWeight: 'bold', marginBottom: 10 }}>ORDER INSIGHT</h6>
                  <div style={{ fontSize: 13 }}>
                    {od.createdAt && (
                      <div className="mb-1">Placed On: {moment(od.createdAt).format('DD/MM/YYYY hh:mm A')}</div>
                    )}
                    {od.unique_invoice && (
                      <div className="mb-1">Unique Invoice: {od.unique_invoice}</div>
                    )}
                    {od.seller_invoice && (
                      <div className="mb-1">Seller Invoice: {od.seller_invoice}</div>
                    )}
                    {od.requested && (
                      <div className="mb-1">
                        Status: <Badge color={getStatusColor(od.requested)} pill className="text-white">{od.requested}</Badge>
                      </div>
                    )}
                    {od.isBulk && <div className="mb-1">Bulk Prepaid</div>}
                    {od.paymentType === 'Partial' && od.partialPyament && (
                      <div className="mb-1">
                        Postpay: &#x20B9;{Number(od.postpayAmt).toLocaleString('en-IN', { maximumFractionDigits: 2, minimumFractionDigits: 2 })}
                      </div>
                    )}
                  </div>
                </Col>
              </Row>
            </div>

            {/* Products Table */}
            <Paper style={{ overflowX: 'auto' }}>
              {od.products && od.products.length > 0 ? (
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Title</TableCell>
                      <TableCell>Packing</TableCell>
                      <TableCell>Expiry</TableCell>
                      <TableCell>Offer</TableCell>
                      <TableCell>MRP</TableCell>
                      <TableCell>PTR</TableCell>
                      <TableCell>Qty</TableCell>
                      <TableCell>Effective PTR</TableCell>
                      <TableCell>GST</TableCell>
                      <TableCell>Final Price</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {od.products.map((p, i) => (
                      <TableRow key={i}>
                        <TableCell>
                          {p.inventory_id && p.inventory_id.medicineTypeName === 'Others' && (
                            <Tooltip title="PCD Product">
                              <span
                                className="size-8 rounded-circle d-inline-block"
                                style={{ backgroundColor: 'orange', marginRight: 4 }}
                              />
                            </Tooltip>
                          )}
                          {p.name}
                          {p.surCharge > 0 && (
                            <>
                              <br />
                              <span className="text-white bg-danger" style={{ padding: '1px 5px', fontSize: 11 }}>
                                Surcharge ({p.surCharge}%)
                              </span>
                            </>
                          )}
                        </TableCell>
                        <TableCell>{p.inventory_id && p.inventory_id.product_id && p.inventory_id.product_id.Type ? p.inventory_id.product_id.Type.name : '-'}</TableCell>
                        <TableCell>{moment(p.expiry_date).format('MM/YYYY')}</TableCell>
                        <TableCell>{getOfferText(p)}</TableCell>
                        <TableCell>&#x20B9;{Number(p.MRP).toFixed(2)}</TableCell>
                        <TableCell>&#x20B9;{Number(p.PTR).toFixed(2)}</TableCell>
                        <TableCell>{p.quantity}</TableCell>
                        <TableCell>{(p.ePTR ? p.ePTR : p.PTR).toFixed(2)}</TableCell>
                        <TableCell>{(p.GST ? p.GST : p.inventory_id && p.inventory_id.product_id && p.inventory_id.product_id.GST ? p.inventory_id.product_id.GST.value : 0)}%</TableCell>
                        <TableCell>&#x20B9;{getFinalValue(p).toFixed(2)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <div style={{ padding: 16 }}>No Items Found</div>
              )}
            </Paper>

            {/* Order Logs */}
            {od.order_status && od.order_status.length > 0 && (
              <div style={{ marginTop: 20 }}>
                <h6 style={{ fontWeight: 'bold', marginBottom: 10 }}>Order Logs</h6>
                <Paper style={{ overflowX: 'auto' }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell align="center">No.</TableCell>
                        <TableCell align="center">Date</TableCell>
                        <TableCell align="center">Status</TableCell>
                        <TableCell align="center">Description</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {od.order_status.map((s, i) => (
                        <TableRow key={i}>
                          <TableCell align="center">{i + 1}</TableCell>
                          <TableCell align="center">{moment(s.date).format('DD/MM/YYYY hh:mm A')}</TableCell>
                          <TableCell align="center">{s.status}</TableCell>
                          <TableCell align="center">{s.admin_description || s.description}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Paper>
              </div>
            )}
          </>
        ) : (
          <div style={{ padding: 20, textAlign: 'center' }}>No order details found</div>
        )}
      </DialogContent>
      <DialogActions>
        {od && (
          <Button
            color="primary"
            onClick={() => window.open(`/admin/orders/orderDetails/${od.order_id}`, '_blank')}
          >
            Open Full Page
          </Button>
        )}
        <Button onClick={onClose} color="secondary">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default OrderDetailsPopup;
